import Phaser from 'phaser';
import { audioManager } from '@/systems/AudioManager';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { MuteToggle } from '@/ui/components/MuteToggle';
import { Layout, Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

export type TrainingPace = 'slow' | 'normal' | 'fast';

export interface TrainingConfig {
  tables: number[];
  timeLimitMs: number;
  paceLabel: string;
}

const PACE_OPTIONS: ReadonlyArray<{ id: TrainingPace; label: string; timeLimitMs: number }> = [
  { id: 'slow', label: 'Devagar', timeLimitMs: 9000 },
  { id: 'normal', label: 'Normal', timeLimitMs: 6000 },
  { id: 'fast', label: 'Rápido', timeLimitMs: 4000 },
];

const ALL_TABLES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

/**
 * TrainingPickScene — escolha de tabuadas + ritmo para o Treino Livre.
 *
 * UX intencionalmente simples: a criança seleciona uma ou mais tabuadas
 * (toques sucessivos alternam) e um ritmo. O botão "Começar" só
 * habilita quando há ≥1 tabuada selecionada.
 */
export class TrainingPickScene extends Phaser.Scene {
  private selectedTables = new Set<number>();
  private pace: TrainingPace = 'normal';
  private tableButtons = new Map<number, Phaser.GameObjects.Container>();
  private paceButtons = new Map<TrainingPace, Phaser.GameObjects.Container>();
  private startButton?: Button;

  constructor() {
    super({ key: 'TrainingPickScene' });
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const { width, height } = this.scale;
    this.selectedTables = new Set();
    this.tableButtons.clear();
    this.paceButtons.clear();

    this.add
      .text(width / 2, 60, 'Treino Livre', {
        fontFamily: Typography.fontFamily,
        fontSize: '48px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, 110, 'Escolha as tabuadas e o ritmo', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    this.renderTableGrid();
    this.renderPaceRow();
    this.renderActions();

    new MuteToggle(this, width - 48, 48);
    new Button(this, 96, height - 48, 'Voltar', {
      width: 160,
      height: 48,
      fontSize: 18,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.primary,
      onClick: () => fadeToScene(this, 'MenuScene'),
    });
  }

  private renderTableGrid(): void {
    const { width, height } = this.scale;
    const cols = 6;
    const buttonSize = 88;
    const gap = 18;
    const totalWidth = cols * buttonSize + (cols - 1) * gap;
    const startX = width / 2 - totalWidth / 2 + buttonSize / 2;
    const startY = height * 0.32;

    ALL_TABLES.forEach((table, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = startX + col * (buttonSize + gap);
      const y = startY + row * (buttonSize + gap);

      const btn = this.makeTableButton(table, x, y, buttonSize);
      this.tableButtons.set(table, btn);
    });

    // Linha de atalhos (Todas / Limpar) abaixo do grid.
    const shortcutsY = startY + 2 * (buttonSize + gap) + 24;
    new Button(this, width / 2 - 110, shortcutsY, 'Todas', {
      width: 180,
      height: 44,
      fontSize: 16,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.accent,
      onClick: () => this.selectAll(),
    });
    new Button(this, width / 2 + 110, shortcutsY, 'Limpar', {
      width: 180,
      height: 44,
      fontSize: 16,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.danger,
      onClick: () => this.clearAll(),
    });
  }

  private makeTableButton(table: number, x: number, y: number, size: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const bg = this.add.graphics();
    const label = this.add
      .text(0, 0, String(table), {
        fontFamily: Typography.fontFamily,
        fontSize: '32px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(size, size);
    container.setInteractive({ useHandCursor: true });
    container.setData({ table, bg });

    this.drawTableButton(container);

    container.on(Phaser.Input.Events.POINTER_UP, () => {
      if (this.selectedTables.has(table)) this.selectedTables.delete(table);
      else this.selectedTables.add(table);
      this.drawTableButton(container);
      this.refreshStartButton();
    });

    return container;
  }

  private drawTableButton(container: Phaser.GameObjects.Container): void {
    const { table, bg } = container.getData(['table', 'bg']) as {
      table: number;
      bg: Phaser.GameObjects.Graphics;
    };
    const size = container.width;
    const selected = this.selectedTables.has(table);
    bg.clear();
    bg.fillStyle(selected ? Palette.accent : Palette.primary, 1);
    bg.fillRoundedRect(-size / 2, -size / 2, size, size, Layout.cornerRadius);
    if (selected) {
      bg.lineStyle(3, Palette.textPrimary, 1);
      bg.strokeRoundedRect(-size / 2, -size / 2, size, size, Layout.cornerRadius);
    }
  }

  private renderPaceRow(): void {
    const { width, height } = this.scale;
    const y = height * 0.74;

    this.add
      .text(width / 2, y - 44, 'Ritmo', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const buttonWidth = 180;
    const gap = 24;
    const totalWidth = PACE_OPTIONS.length * buttonWidth + (PACE_OPTIONS.length - 1) * gap;
    const startX = width / 2 - totalWidth / 2 + buttonWidth / 2;

    PACE_OPTIONS.forEach((option, index) => {
      const container = this.add.container(startX + index * (buttonWidth + gap), y);
      const bg = this.add.graphics();
      const label = this.add
        .text(0, 0, option.label, {
          fontFamily: Typography.fontFamily,
          fontSize: '22px',
          color: Palette.textPrimaryCss,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      container.add([bg, label]);
      container.setSize(buttonWidth, 56);
      container.setInteractive({ useHandCursor: true });
      container.setData({ id: option.id, bg });

      container.on(Phaser.Input.Events.POINTER_UP, () => {
        this.pace = option.id;
        this.refreshPaceButtons();
      });

      this.paceButtons.set(option.id, container);
    });

    this.refreshPaceButtons();
  }

  private refreshPaceButtons(): void {
    this.paceButtons.forEach((container, id) => {
      const { bg } = container.getData(['bg']) as { bg: Phaser.GameObjects.Graphics };
      const w = container.width;
      const h = container.height;
      const active = this.pace === id;
      bg.clear();
      bg.fillStyle(active ? Palette.warning : 0x2a2a4a, 1);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, Layout.cornerRadius);
      if (active) {
        bg.lineStyle(2, Palette.textPrimary, 1);
        bg.strokeRoundedRect(-w / 2, -h / 2, w, h, Layout.cornerRadius);
      }
    });
  }

  private renderActions(): void {
    const { width, height } = this.scale;
    this.startButton = new Button(this, width / 2, height - 110, 'Começar', {
      width: 320,
      height: 72,
      fontSize: 28,
      onClick: () => this.start(),
    });
    this.refreshStartButton();
  }

  private refreshStartButton(): void {
    if (!this.startButton) return;
    const enabled = this.selectedTables.size > 0;
    this.startButton.setAlpha(enabled ? 1 : 0.4);
    this.startButton.disableInteractive();
    if (enabled) this.startButton.setInteractive({ useHandCursor: true });
  }

  private selectAll(): void {
    this.selectedTables = new Set(ALL_TABLES);
    this.tableButtons.forEach((c) => this.drawTableButton(c));
    this.refreshStartButton();
  }

  private clearAll(): void {
    this.selectedTables.clear();
    this.tableButtons.forEach((c) => this.drawTableButton(c));
    this.refreshStartButton();
  }

  private start(): void {
    if (this.selectedTables.size === 0) return;
    audioManager.unlock();
    const pace = PACE_OPTIONS.find((p) => p.id === this.pace)!;
    const config: TrainingConfig = {
      tables: [...this.selectedTables].sort((a, b) => a - b),
      timeLimitMs: pace.timeLimitMs,
      paceLabel: pace.label,
    };
    fadeToScene(this, 'TrainingScene', config);
  }
}
